<template>
  <div class="page-container training-page">
    <component
      v-for="(block, index) in sortedBlocks"
      :key="`block-${index}-${block.__component}`"
      :is="getComponentName(block.__component)"
      v-bind="getComponentProps(block)"
    />
  </div>
</template>

<script>
import TrainingSectionHero from './components/TrainingSectionHero.vue';
import TrainingSectionDocuments from './components/TrainingSectionDocuments.vue';
import TrainingSectionContent from './components/TrainingSectionContent.vue';
import TrainingSectionGallery from './components/TrainingSectionGallery.vue';
import TrainingSectionInfoBlocks from './components/TrainingSectionInfoBlocks.vue';
import TrainingSectionIconBlocks from './components/TrainingSectionIconBlocks.vue';
import TrainingSectionIconBlocksAccordion from './components/TrainingSectionIconBlocksAccordion.vue';
import TrainingSectionGridBlocks from './components/TrainingSectionGridBlocks.vue';
import api from '../../tools/api';

export default {
  name: 'Training',
  components: {
    TrainingSectionHero,
    TrainingSectionDocuments,
    TrainingSectionContent,
    TrainingSectionGallery,
    TrainingSectionInfoBlocks,
    TrainingSectionIconBlocks,
    TrainingSectionIconBlocksAccordion,
    TrainingSectionGridBlocks,
  },
  data() {
    return {
      pageContent: {},
    };
  },
  computed: {
    lang() {
      return this.$store.getters.getLang;
    },
    sortedBlocks() {
      if (!this.pageContent?.blocks || !Array.isArray(this.pageContent.blocks)) {
        return [];
      }
      // Фильтруем только блоки training-page-sections и сортируем по Order
      return this.pageContent.blocks
        // eslint-disable-next-line no-underscore-dangle
        .filter((block) => block.__component && block.__component.startsWith('training-page-sections.'));
    },
  },
  methods: {
    isPreview() {
      return this.$route.query.preview === 'true';
    },
    async getPageContent() {
      try {
        const previewParam = this.isPreview() ? '_publicationState=preview' : '';
        const results = await api('page-training', true, previewParam);
        this.pageContent = results.data || {};
      } catch (error) {
        console.error('Error loading training page content:', error);
        this.pageContent = {};
      }
    },
    getComponentName(componentType) {
      // eslint-disable-next-line no-underscore-dangle
      if (!componentType) return null;

      // Извлекаем тип блока из строки типа "training-page-sections.training-section-hero"
      const blockType = componentType.replace('training-page-sections.', '');

      // Маппинг типов блоков на имена компонентов
      const componentMap = {
        'training-section-hero': 'TrainingSectionHero',
        'training-section-documents': 'TrainingSectionDocuments',
        'training-section-content': 'TrainingSectionContent',
        'training-section-gallery': 'TrainingSectionGallery',
        'training-section-info-blocks': 'TrainingSectionInfoBlocks',
        'training-section-icon-blocks': 'TrainingSectionIconBlocks',
        'training-section-icon-blocks-accordion': 'TrainingSectionIconBlocksAccordion',
        'training-section-grid-blocks': 'TrainingSectionGridBlocks',
      };

      return componentMap[blockType] || null;
    },
    getComponentProps(block) {
      // eslint-disable-next-line no-underscore-dangle
      const blockType = block.__component?.replace('training-page-sections.', '') || '';

      switch (blockType) {
        case 'training-section-hero':
          return {
            subtitle: block.Subtitle || '',
            title: block.Title || '',
            background: block.Background || null,
            backgroundMobile: block.BackgroundMobile || null,
            companyFullName: block.CompanyFullName || '',
            companyFullNameLabel: block.CompanyFullNameLabel || '',
            companyShortName: block.CompanyShortName || '',
            companyShortNameLabel: block.CompanyShortNameLabel || '',
            companyCreationDate: block.CompanyCreationDate || '',
            companyCreationDateLabel: block.CompanyCreationDateLabel || '',
            companyFounder: block.CompanyFounder || '',
            companyFounderLabel: block.CompanyFounderLabel || '',
            companyLocation: block.CompanyLocation || '',
            companyLocationLabel: block.CompanyLocationLabel || '',
            workScheduleTitle: block.WorkScheduleTitle || '',
            workScheduleDescription: block.WorkScheduleDescription || '',
            workScheduleWeekdays: block.WorkScheduleWeekdays || '',
            workScheduleLunch: block.WorkScheduleLunch || '',
            contactPhone: block.ContactPhone || '',
            contactEmail: block.ContactEmail || '',
            contactSectionTitle: block.ContactSectionTitle || '',
            activityLocation1: block.ActivityLocation1 || '',
            activityLocation2: block.ActivityLocation2 || '',
            activityLocationTitle: block.ActivityLocationTitle || '',
          };

        case 'training-section-documents':
          return {
            title: block.Title || '',
            theme: block.Theme || 'light',
            documents: (block.Documents || []).map((doc) => ({
              Title: doc.Title || '',
              Document: doc.Document || null,
            })),
          };

        case 'training-section-gallery':
          return {
            title: block.Title || '',
            theme: block.Theme || 'light',
            galleryItems: (block.GalleryItems || []).map((item) => ({
              Title: item.Title || '',
              Description: item.Description || '',
              Image: item.Image || null,
              ImageMobile: item.ImageMobile || null,
            })),
          };

        case 'training-section-info-blocks':
          return {
            title: block.Title || '',
            infoBlocks: (block.InfoBlocks || []).map((infoBlock) => ({
              Title: infoBlock.Title || '',
              Text: infoBlock.Text || '',
              Image: infoBlock.Image || null,
              ImageMobile: infoBlock.ImageMobile || null,
              ImagePosition: infoBlock.ImagePosition || 'top',
            })),
          };

        case 'training-section-icon-blocks':
          return {
            title: block.Title || '',
            subtitle: block.Subtitle || '',
            iconBlocks: (block.IconBlocks || []).map((iconBlock) => ({
              Title: iconBlock.Title || '',
              Text: iconBlock.Text || '',
              Icon: iconBlock.Icon || null,
            })),
          };

        case 'training-section-icon-blocks-accordion':
          return {
            title: block.Title || '',
            showBottomLine: block.ShowBottomLine !== false,
            programs: (block.Programs || []).map((program) => ({
              Name: program.Name || '',
              OpenByDefault: !!program.OpenByDefault,
              IconBlocks: (program.IconBlocks || []).map((iconBlock) => ({
                Title: iconBlock.Title || '',
                Text: iconBlock.Text || '',
                Icon: iconBlock.Icon || null,
              })),
            })),
          };

        case 'training-section-content':
          return {
            title: block.Title || '',
            text: block.Text || '',
            theme: block.Theme || 'light',
            image: block.Image || null,
            imageMobile: block.ImageMobile || null,
          };

        case 'training-section-grid-blocks':
          return {
            leftBlock: block.LeftBlock || [],
            rightBlocks: block.RightBlocks || [],
          };

        default:
          return {};
      }
    },
  },
  created() {
    this.getPageContent();
  },
  watch: {
    lang() {
      this.getPageContent();
    },
  },
};
</script>

<style lang="scss" scoped>
.training-page {
  background-color: #f3f3f3;
}
</style>
